import moment from "moment";

import {
  DIRECT_MAIL_CAMPAIGN_TYPE,
  DIRECT_MAIL_CAMP_PROVIDER,
} from "@/features/campaigns/direct-mail/utils/directMailConstants";
import {
  calculateRRDStoreCopyCost,
  getRRDStoreCopyDefaultQuantity,
  getRRDStoreCopyFormFields,
  resolveRRDStoreCopyConfig,
  validateRRDStoreCopyQuantity,
} from "@/features/campaigns/direct-mail/rrd/utils/rrdStoreCopyConfigHelper";

export const NATIONAL_PLUS_UP_STORE_COPY_ERROR =
  "Store copy must be ordered in increments of 1,000 (minimum 1,000).";

export const NATIONAL_PLUS_UP_LOCATION_OPTION_ERROR =
  "Please opt in to at least one option (Plus-Up Distribution or Store Copy) to join this location.";

const NATIONAL_PLUS_UP_LEGACY_MAX_STORE_COPY_QNT = 999999000;

type NationalPlusUpRecord = Record<string, any>;

type NationalPlusUpStoreCopyConfig = {
  cpm?: number;
  minQuantity?: number;
  maxQuantity?: number;
  isConfigured?: boolean;
  source?: string;
} & NationalPlusUpRecord;

type NationalPlusUpCostArgs = {
  distribution?: boolean;
  plusUpCost?: number | string;
  storeCopy?: boolean;
  storeCopyQnt?: number | string;
  storeCopyCpm?: number | string;
  storeCopyConfig?: NationalPlusUpStoreCopyConfig;
};

type NationalPlusUpDraftPayloadArgs = {
  campaign?: NationalPlusUpRecord;
  selectedBrand?: NationalPlusUpRecord;
  loggedInUser?: NationalPlusUpRecord;
  selections?: NationalPlusUpRecord[];
  isDraft?: boolean;
  removeOrderFromDraftOrderIds?: string[];
};

type NationalPlusUpConfirmPayloadArgs = {
  draftData?: NationalPlusUpRecord;
  totalBudget?: number;
  loggedInUser?: NationalPlusUpRecord;
  selectedBrand?: NationalPlusUpRecord;
  paymentStatus?: number;
};

export function isNationalPlusUpCampaign(campaign: NationalPlusUpRecord = {}) {
  const type = normalizeType(campaign?.campaign_type);
  return type === DIRECT_MAIL_CAMPAIGN_TYPE.NATIONAL_PLUS_UP;
}

export function formatNationalMailingWeek(
  date: string | null | undefined,
  format = "MMM D, YYYY"
) {
  const selectedSunday = parseDate(date);
  if (!date || !selectedSunday.isValid()) return "N/A";
  const start = selectedSunday.clone().add(1, "day");
  const end = start.clone().add(4, "days");
  return `${start.format(format)} - ${end.format(format)}`;
}

export function formatNationalPlusUpJoinBy(date: string | null | undefined) {
  const parsed = parseDate(date);
  if (!date || !parsed.isValid()) return "N/A";
  return `Join by ${parsed.format("MMMM D, YYYY")} at 11:59PM`;
}

export function formatNationalPlusUpLocationDisplayName(
  location: NationalPlusUpRecord = {}
) {
  const locationName =
    location?.location_name ||
    location?.locationName ||
    location?.name ||
    "N/A";
  const marketName = location?.market_name || location?.marketName;

  return [locationName, marketName].filter(Boolean).join(" : ");
}

function hasExplicitStoreCopyConfig(location: NationalPlusUpRecord = {}) {
  return Boolean(
    location.store_copy_config ||
      location.store_copy_min_quantity ||
      location.store_copy_max_quantity
  );
}

function resolveNationalPlusUpStoreCopyConfig({
  location = {},
  campaign = {},
}: {
  location?: NationalPlusUpRecord;
  campaign?: NationalPlusUpRecord;
}) {
  if (hasExplicitStoreCopyConfig(location)) {
    return resolveRRDStoreCopyConfig({ location, campaign });
  }

  const cpm =
    Number(location.store_copy_cpm ?? campaign.storeCopyCpm ?? 0) || 0;

  return {
    cpm,
    minQuantity: 1000,
    maxQuantity: NATIONAL_PLUS_UP_LEGACY_MAX_STORE_COPY_QNT,
    isConfigured: true,
    source: "national-plus-up-legacy",
  };
}

export function validateNationalPlusUpStoreCopyQuantity(
  storeCopyQnt: number | string | null | undefined,
  storeCopy: boolean,
  storeCopyConfig?: NationalPlusUpStoreCopyConfig
) {
  if (!storeCopy) return "";

  if (!storeCopyConfig) {
    const stringValue = String(storeCopyQnt ?? "");
    const numValue = Number(storeCopyQnt);

    if (
      storeCopyQnt === null ||
      storeCopyQnt === undefined ||
      stringValue.trim() === "" ||
      Number.isNaN(numValue) ||
      numValue < 1000 ||
      numValue % 1000 !== 0
    ) {
      return NATIONAL_PLUS_UP_STORE_COPY_ERROR;
    }

    return "";
  }

  const validationMessage = validateRRDStoreCopyQuantity({
    storeCopyQnt,
    storeCopy,
    config: storeCopyConfig,
  });

  return validationMessage;
}

export function calculateNationalPlusUpCosts({
  distribution = false,
  plusUpCost = 0,
  storeCopy = false,
  storeCopyQnt = 0,
  storeCopyCpm = 0,
  storeCopyConfig,
}: NationalPlusUpCostArgs) {
  const plus_up_cost = distribution ? Number(plusUpCost) || 0 : 0;
  const store_copy_cost = storeCopyConfig
    ? calculateRRDStoreCopyCost({
        storeCopy,
        storeCopyQnt,
        config: storeCopyConfig,
      }) || 0
    : storeCopy && !validateNationalPlusUpStoreCopyQuantity(storeCopyQnt, true)
      ? (Number(storeCopyQnt) / 1000) * (Number(storeCopyCpm) || 0)
      : 0;

  return {
    plus_up_cost,
    store_copy_cost,
    total_cost: plus_up_cost + store_copy_cost,
  };
}

export function getNationalPlusUpInitialValues(
  campaign: NationalPlusUpRecord = {},
  draftData: NationalPlusUpRecord = {}
) {
  const locations = campaign?.eligible_location || [];
  const selections = draftData?.selections || [];

  return locations.reduce((acc: NationalPlusUpRecord, location) => {
    const locationId = getLocationId(location);
    const draftLocation = selections.find(
      (selection) => String(selection?.locationId) === String(locationId)
    );
    const distributionData = getDistributionData(location);
    const distributionAvailable = distributionData.quantity > 0;
    const draftDistribution =
      distributionAvailable &&
      Boolean(draftLocation?.distribution ?? draftLocation?.plusUpDistribution);
    const draftStoreCopy = Boolean(draftLocation?.storeCopy);
    const storeCopyConfig = resolveNationalPlusUpStoreCopyConfig({
      location,
      campaign,
    });
    const storeCopyFields = getRRDStoreCopyFormFields(storeCopyConfig);
    const fixedStoreCopyQnt = getRRDStoreCopyDefaultQuantity(storeCopyConfig);
    const draftStoreCopyQnt = draftLocation?.storeCopyQnt || "";
    const storeCopyQnt =
      draftStoreCopy && fixedStoreCopyQnt
        ? fixedStoreCopyQnt
        : draftStoreCopy &&
            !validateNationalPlusUpStoreCopyQuantity(
              draftStoreCopyQnt,
              true,
              storeCopyConfig
            )
          ? draftStoreCopyQnt
          : "";
    const costData = calculateNationalPlusUpCosts({
      distribution: draftDistribution,
      plusUpCost: distributionData.cost,
      storeCopy: draftStoreCopy,
      storeCopyQnt,
      storeCopyConfig,
    });

    acc[locationId] = {
      location_id: locationId,
      location_name: getLocationName(location),
      system_generated_location_id:
        location?.sys_location_id ||
        location?.system_generated_location_id ||
        "",
      market_name: location?.market_name || "",
      address: getLocationAddress(location),
      print_type:
        location?.print_type || campaign?.campaignLevelPrintType || "",
      hero_preview: campaign?.hero_image || "",
      national_mailing_week: campaign?.mailing_week || "",
      distribution: draftDistribution,
      distribution_available: distributionAvailable,
      estimated_distribution: distributionData.quantity,
      plus_up_cost_admin: distributionData.cost,
      plus_up_cost: costData.plus_up_cost,
      store_copy: draftStoreCopy,
      store_copy_qnt: storeCopyQnt,
      store_copy_cost: costData.store_copy_cost,
      ...storeCopyFields,
      total_cost: costData.total_cost,
      orderId: draftLocation?.orderId || null,
    };

    return acc;
  }, {});
}

export function isNationalPlusUpLocationJoinable(
  location: NationalPlusUpRecord = {}
) {
  const hasDistribution = Boolean(location?.distribution);
  const storeCopyConfig = hasExplicitStoreCopyConfig(location)
    ? location
    : undefined;
  const hasInvalidStoreCopy =
    Boolean(location?.store_copy) &&
    Boolean(
      validateNationalPlusUpStoreCopyQuantity(
        location?.store_copy_qnt,
        location?.store_copy,
        storeCopyConfig
      )
    );
  const hasValidStoreCopy =
    Boolean(location?.store_copy) &&
    !validateNationalPlusUpStoreCopyQuantity(
      location?.store_copy_qnt,
      location?.store_copy,
      storeCopyConfig
    );

  if (hasInvalidStoreCopy) return false;

  return hasDistribution || hasValidStoreCopy;
}

export function fetchNationalPlusUpJoinableLocations(
  locations: NationalPlusUpRecord = {}
) {
  return Object.values(locations).filter(isNationalPlusUpLocationJoinable);
}

export function getNationalPlusUpLocationsTotal(
  locations: NationalPlusUpRecord[] = []
) {
  return locations.reduce((total, location) => {
    return total + (Number(location?.total_cost || location?.totalCost) || 0);
  }, 0);
}

export function formatNationalPlusUpSelectedLocations(
  values: NationalPlusUpRecord = {},
  campaign: NationalPlusUpRecord = {}
) {
  return fetchNationalPlusUpJoinableLocations(values).map((location) => {
    const sourceLocation = findCampaignLocation(campaign, location.location_id);
    const locationInfo = formatLocationInfoForRRDPayload(sourceLocation);
    const costs = calculateNationalPlusUpCosts({
      distribution: location.distribution,
      plusUpCost: location.plus_up_cost_admin,
      storeCopy: location.store_copy,
      storeCopyQnt: location.store_copy_qnt,
      storeCopyCpm: location.store_copy_cpm,
      storeCopyConfig: location,
    });

    return {
      locationInfo,
      selectedAssets: {
        font: {
          hero: null,
          coupons: [],
        },
        back: {
          hero: null,
          coupons: [],
        },
      },
      locationId: location.location_id,
      locationName: location.location_name,
      systemGeneratedLocationId: location.system_generated_location_id,
      sysLocationId: location.system_generated_location_id,
      heroImages: [],
      coupons: [],
      distribution: Boolean(location.distribution),
      estimatedDistribution: location.distribution
        ? Number(location.estimated_distribution) || 0
        : 0,
      distributionCost: costs.plus_up_cost,
      storeCopy: Boolean(location.store_copy),
      storeCopyQnt: location.store_copy
        ? Number(location.store_copy_qnt) || 0
        : 0,
      storeCopyCost: costs.store_copy_cost,
      storeCopyCpm: Number(location.store_copy_cpm) || 0,
      mailingDate: location.national_mailing_week,
      differentCouponExpiry: false,
      frontCouponExpiryDate: "",
      backCouponExpiryDate: "",
      totalCost: costs.total_cost,
      printType: location.print_type,
      orderId: location.orderId || null,
    };
  });
}

export function buildNationalPlusUpDraftPayload({
  campaign = {},
  selectedBrand = {},
  loggedInUser = {},
  selections = [],
  isDraft = true,
  removeOrderFromDraftOrderIds = [],
}: NationalPlusUpDraftPayloadArgs) {
  return {
    is_draft: isDraft,
    selections,
    brandId: selectedBrand?.id,
    userId: loggedInUser?.id,
    participantId: loggedInUser?.participantData?.id,
    sysParticipantId:
      loggedInUser?.participantData?.system_generated_participant_id,
    campaignId: campaign?.campaignId,
    sysCampaignId: campaign?.systemGeneratedCampaignId,
    campaignName: campaign?.campaign_name,
    campaignType: campaign?.campaign_type,
    paymentMethod: campaign?.paymentMethod,
    adminMinimumCouponDurationDays: campaign?.adminMinimumCouponDurationDays,
    adminCouponExpirationDate: campaign?.adminCouponExpirationDate,
    totalBudget: getNationalPlusUpLocationsTotal(selections),
    removeOrderFromDraftOrderIds,
  };
}

export function buildNationalPlusUpConfirmPayload({
  draftData = {},
  totalBudget = 0,
  loggedInUser = {},
  selectedBrand = {},
  paymentStatus = 0,
}: NationalPlusUpConfirmPayloadArgs) {
  return {
    orderIds: draftData?.orderIds,
    locationData: draftData?.locationData,
    payment_status: paymentStatus,
    total_budget: totalBudget,
    campaign_cat: "Postcard",
    provider: DIRECT_MAIL_CAMP_PROVIDER.RRD,
    id: draftData?.campaignId,
    fk_campaign_id: draftData?.campaignId,
    fk_campaign_name: draftData?.campaignName,
    user_id: loggedInUser?.id,
    user_name: `${loggedInUser?.first_name} ${loggedInUser?.last_name}`,
    system_generated_participant_id:
      loggedInUser?.participantData?.system_generated_participant_id,
    fk_participant_id: loggedInUser?.participantData?.id,
    fk_brand_id: selectedBrand?.id,
    system_generated_brand_id: selectedBrand?.system_generated_brand_id,
    fk_brand_name: selectedBrand?.brand_name,
    is_white_label: selectedBrand?.is_white_label,
  };
}

export function getNationalPlusUpNotEligibleLocations(
  campaign: NationalPlusUpRecord = {}
) {
  return campaign?.not_eligible_locations || [];
}

function normalizeType(type: string = "") {
  return String(type)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function getLocationId(location: NationalPlusUpRecord = {}) {
  return location?.id || location?.location_id;
}

function getLocationName(location: NationalPlusUpRecord = {}) {
  return location?.location_name || "N/A";
}

function getDistributionData(location: NationalPlusUpRecord = {}) {
  const distribution = location?.distribution || {};
  return {
    quantity: Number(
      distribution?.quantity || location?.estimated_distribution || 0
    ),
    cost: Number(distribution?.cost || 0),
  };
}

function getLocationAddress(location: NationalPlusUpRecord = {}) {
  if (location?.delivery_address) return location.delivery_address;

  const address = [
    location?.street_address || location?.store_address,
    location?.city || location?.store_city,
    location?.state || location?.store_state,
    location?.zip_code || location?.store_home_zip,
  ]
    .filter(Boolean)
    .join(", ");

  return address;
}

function findCampaignLocation(
  campaign: NationalPlusUpRecord = {},
  locationId: unknown
) {
  const locations = campaign?.eligible_location || [];
  return locations.find((location) => {
    return String(getLocationId(location)) === String(locationId);
  });
}

function formatLocationInfoForRRDPayload(location: NationalPlusUpRecord = {}) {
  return {
    ...location,
    assets: normalizeRRDAssets(location?.assets),
  };
}

function normalizeRRDAssets(assets: NationalPlusUpRecord = {}) {
  return {
    postcard: Array.isArray(assets?.postcard) ? assets.postcard : [],
    value: Array.isArray(assets?.value) ? assets.value : [],
  };
}

function parseDate(date: string | null | undefined) {
  return moment(
    date,
    [moment.ISO_8601, "YYYY-MM-DD", "MM/DD/YYYY", "MMM D, YYYY"],
    true
  );
}
